import {
  getRunTimeHours,
  HydratedItem,
  Item,
  resolveRunTimeFormat,
  RunTimeFormat,
} from "@forgepoint/shared";
import Decimal from "decimal.js";

/**
 * Parses a finite decimal value.
 * @param value - Decimal string to parse
 * @returns The parsed Decimal or null when invalid
 */
export function parseDecimal(value: string): Decimal | null {
  try {
    const decimal = new Decimal(value);
    return decimal.isFinite() ? decimal : null;
  } catch {
    return null;
  }
}

/**
 * Formats a stored native runtime value for the BOM operation summary.
 * @param runTimeValue - Runtime value expressed in the selected format
 * @param quantityPerValue - Production-basis quantity required per parent item
 * @param format - Runtime value format
 * @returns Human-readable runtime text
 */
export function formatRunTime(
  runTimeValue: string | undefined,
  quantityPerValue: string | undefined,
  format: RunTimeFormat | null | undefined,
): string {
  if (!runTimeValue) {
    return "0 hours / piece";
  }
  const quantityPer = quantityPerValue ?? "1";
  const resolvedFormat = resolveRunTimeFormat(format);
  if (getRunTimeHours(runTimeValue, resolvedFormat, quantityPer) === null) {
    return "Invalid runtime";
  }
  if (resolvedFormat === "PIECES_PER_HOUR") {
    return `${runTimeValue} pieces / hour`;
  }
  if (resolvedFormat === "MINUTES_PER_PIECE") {
    return `${runTimeValue} minutes / piece`;
  }
  return `${runTimeValue} hours / piece`;
}

/**
 * Gets the appropriate unit cost for a given quantity from the item's cost breaks.
 *
 * Returns the unit cost from the cost break with the highest minQty that is <= quantity.
 * Returns null if no cost breaks exist, item is null, or no cost break applies to the quantity.
 *
 * This mirrors the backend ItemsService.getCostForQuantity logic but works client-side
 * with already-loaded item data.
 *
 * @param item - The item to get the cost for
 * @param quantity - The quantity to calculate cost for
 * @returns The unit cost as a string formatted to 2 decimal places, or null if no cost applies
 */
export function getUnitCostForQuantity(
  item: Item | null,
  quantity: number,
): string | null {
  if (!item?.costBreaks || item.costBreaks.length === 0) {
    return null;
  }

  const applicableCostBreaks = item.costBreaks
    .filter((costBreak) => costBreak.minQty <= quantity)
    .sort((a, b) => b.minQty - a.minQty);

  if (applicableCostBreaks.length === 0) {
    return null;
  }

  return applicableCostBreaks[0].unitCost;
}

/**
 * Picks the fresher of a tree-embedded item copy and the store's own entry
 * for the same item, by updatedAt.
 */
export function resolveDisplayItem(
  embedded: HydratedItem,
  ownEntry: HydratedItem | undefined,
): HydratedItem {
  if (!ownEntry || ownEntry.id !== embedded.id) {
    return embedded;
  }
  return ownEntry.updatedAt.getTime() > embedded.updatedAt.getTime()
    ? ownEntry
    : embedded;
}
